import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import type { FormEvent } from "react";
import {
  Check,
  ChevronRight,
  CircleAlert,
  ExternalLink,
  Globe2,
  LoaderCircle,
  Search,
  Send,
  Sparkles,
} from "lucide-react";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { NanaeLogoFull } from "@/components/qv3";
import { SUPABASE_ANON_KEY, SUPABASE_URL, supabase } from "@/lib/supabase";
import "@/styles/seo-cockpit.css";

type RunState = "idle" | "submitting" | "pending" | "error";
type OptimizationMode = "seo" | "aeo" | "geo" | "complete";

const OPTIMIZATION_MODES: Array<{
  value: OptimizationMode;
  acronym: string;
  label: string;
  description: string;
}> = [
  {
    value: "seo",
    acronym: "SEO",
    label: "Recherche Google",
    description: "Positionnement, structure et maillage.",
  },
  {
    value: "aeo",
    acronym: "AEO",
    label: "Réponses directes",
    description: "Questions, définitions et réponses extractibles.",
  },
  {
    value: "geo",
    acronym: "GEO",
    label: "Citations IA",
    description: "Sources, preuves et passages faciles à citer.",
  },
  {
    value: "complete",
    acronym: "360°",
    label: "Optimisation complète",
    description: "SEO, AEO, GEO et lisibilité par les IA.",
  },
];

const OBJECTIVE_SUGGESTIONS: Record<OptimizationMode, string[]> = {
  seo: [
    "Trouver les requêtes les plus pertinentes et améliorer le positionnement de cette page.",
    "Clarifier l'offre et attirer davantage de visiteurs qualifiés depuis Google.",
    "Optimiser le titre, la description, la structure et les liens internes.",
  ],
  aeo: [
    "Identifier les questions des prospects et y répondre clairement sur cette page.",
    "Transformer cette page en réponse de référence sur son sujet principal.",
    "Ajouter des réponses directes, comparaisons et définitions réellement utiles.",
  ],
  geo: [
    "Rendre cette page plus fiable, vérifiable et facile à citer par les moteurs IA.",
    "Renforcer les preuves, les sources et la clarté des informations importantes.",
    "Faire comprendre précisément l'entreprise, son expertise et son offre aux IA.",
  ],
  complete: [
    "Choisir automatiquement la meilleure stratégie pour cette page.",
    "Attirer des prospects qualifiés depuis Google et les moteurs de réponse IA.",
    "Améliorer à la fois le positionnement, les réponses directes et la citabilité.",
  ],
};

type SeoRunResponse = {
  runId: string;
  status: "pending_review";
  reviewUrl?: string;
  message?: string;
};

const GOTOHUMAN_INBOX_URL =
  "https://app.gotohuman.com/accounts/TScewll7ks7ggkqm3ZUJ/inbox/agents/5o6ToxvwcrcjNvtJJf2k";

function getReviewUrl(value: unknown) {
  if (typeof value !== "string") return GOTOHUMAN_INBOX_URL;
  try {
    const url = new URL(value);
    return url.protocol === "https:" && url.hostname.endsWith("gotohuman.com")
      ? url.toString()
      : GOTOHUMAN_INBOX_URL;
  } catch {
    return GOTOHUMAN_INBOX_URL;
  }
}

export const Route = createFileRoute("/seo")({
  head: () => ({
    meta: [
      { title: "Pilotage SEO — Hōjin" },
      {
        name: "description",
        content: "Analysez une page, générez une proposition SEO et gardez la validation finale.",
      },
    ],
  }),
  component: SeoCockpitRoute,
});

function SeoCockpitRoute() {
  return (
    <ProtectedRoute>
      <SeoCockpit />
    </ProtectedRoute>
  );
}

function SeoCockpit() {
  const [pageUrl, setPageUrl] = useState("https://hojin.ai/");
  const [topic, setTopic] = useState("");
  const [optimizationMode, setOptimizationMode] = useState<OptimizationMode>("complete");
  const [state, setState] = useState<RunState>("idle");
  const [result, setResult] = useState<SeoRunResponse | null>(null);
  const [error, setError] = useState("");

  const hostname = useMemo(() => {
    try {
      return new URL(pageUrl).hostname;
    } catch {
      return pageUrl || "votre site";
    }
  }, [pageUrl]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    setState("submitting");
    const validationWindow = window.open("", "hojin-gotohuman-review");

    if (validationWindow) {
      validationWindow.document.title = "Hōjin prépare votre validation…";
      validationWindow.document.body.innerHTML =
        '<p style="font:16px system-ui;padding:32px">Hōjin prépare votre proposition…</p>';
    }

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error("Votre session a expiré. Reconnectez-vous puis réessayez.");
      }

      const response = await fetch(`${SUPABASE_URL}/functions/v1/seo-generate`, {
        method: "POST",
        headers: {
          apikey: SUPABASE_ANON_KEY,
          authorization: `Bearer ${session.access_token}`,
          "content-type": "application/json",
        },
        body: JSON.stringify({
          pageUrl,
          topic: topic.trim() || "Analyse automatique de la page et choix du meilleur objectif.",
          optimizationMode,
          locale: "fr-FR",
        }),
      });
      const payload = (await response.json().catch(() => null)) as
        | SeoRunResponse
        | { message?: string }
        | null;

      if (!response.ok) {
        throw new Error(payload?.message ?? "Le moteur SEO n'est pas disponible pour le moment.");
      }

      setResult(payload as SeoRunResponse);
      setState("pending");
      if (validationWindow) {
        validationWindow.location.href = getReviewUrl((payload as SeoRunResponse).reviewUrl);
      }
    } catch (cause) {
      validationWindow?.close();
      setError(cause instanceof Error ? cause.message : "Une erreur inattendue est survenue.");
      setState("error");
    }
  }

  return (
    <div className="seo-shell">
      <header className="seo-topbar">
        <Link to="/" aria-label="Retour à l'accueil Hōjin">
          <NanaeLogoFull symbolSize="md" wordmarkSize="md" />
        </Link>
        <nav aria-label="Navigation SEO">
          <span className="seo-topbar-current">Pilotage SEO</span>
          <Link to="/foyer">Le Foyer</Link>
          <Link to="/compte/parametres">Paramètres</Link>
        </nav>
      </header>

      <main className="seo-main">
        <section className="seo-hero">
          <div>
            <p className="seo-kicker">Hōjin Autopilot · Visibilité</p>
            <h1>
              Votre visibilité,
              <br />
              sans la plomberie.
            </h1>
            <p className="seo-lead">
              Donnez une page et un objectif. Hōjin optimise sa visibilité dans Google et les
              moteurs de réponse IA. Vous gardez le dernier mot.
            </p>
          </div>
          <div className="seo-status-card" aria-label="État des connexions">
            <ConnectionRow label="Google Search Console" detail="Connecté" />
            <ConnectionRow label="Validation humaine" detail="Connectée" />
            <ConnectionRow label="Publication GitHub" detail="Connectée" />
          </div>
        </section>

        <section className="seo-workspace">
          <div className="seo-stepper" aria-label="Progression">
            <Step number="01" label="Demande" active={state !== "pending"} />
            <Step number="02" label="Proposition" active={state === "pending"} />
            <Step number="03" label="Publication" />
          </div>

          {state !== "pending" ? (
            <form className="seo-panel" onSubmit={submit}>
              <div className="seo-panel-heading">
                <div className="seo-icon-box">
                  <Search aria-hidden="true" />
                </div>
                <div>
                  <p className="seo-panel-overline">Nouvelle optimisation unifiée</p>
                  <h2>Quelle page doit progresser&nbsp;?</h2>
                </div>
              </div>

              <label className="seo-field">
                <span>Adresse de la page</span>
                <div className="seo-input-wrap">
                  <Globe2 aria-hidden="true" />
                  <input
                    type="url"
                    required
                    value={pageUrl}
                    onChange={(event) => setPageUrl(event.target.value)}
                    placeholder="https://votre-site.fr/page"
                  />
                </div>
              </label>

              <fieldset className="seo-mode-field">
                <legend>Type d'optimisation</legend>
                <div className="seo-mode-grid">
                  {OPTIMIZATION_MODES.map((mode) => (
                    <label
                      key={mode.value}
                      className={`seo-mode-card${
                        optimizationMode === mode.value ? " is-selected" : ""
                      }`}
                    >
                      <input
                        type="radio"
                        name="optimization-mode"
                        value={mode.value}
                        checked={optimizationMode === mode.value}
                        onChange={() => setOptimizationMode(mode.value)}
                      />
                      <span className="seo-mode-acronym">{mode.acronym}</span>
                      <span className="seo-mode-copy">
                        <strong>{mode.label}</strong>
                        <small>{mode.description}</small>
                      </span>
                    </label>
                  ))}
                </div>
              </fieldset>

              <label className="seo-field">
                <span>Sujet ou objectif · facultatif</span>
                <textarea
                  rows={4}
                  value={topic}
                  onChange={(event) => setTopic(event.target.value)}
                  placeholder="Laissez vide : Hōjin analysera la page et choisira l’objectif le plus pertinent."
                />
              </label>

              <div className="seo-suggestions" aria-label="Objectifs suggérés">
                <span>Ou choisissez une proposition</span>
                <div>
                  {OBJECTIVE_SUGGESTIONS[optimizationMode].map((suggestion) => (
                    <button
                      key={suggestion}
                      type="button"
                      className={topic === suggestion ? "is-selected" : ""}
                      onClick={() => setTopic(suggestion)}
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>

              {state === "error" && (
                <div className="seo-error" role="alert">
                  <CircleAlert aria-hidden="true" />
                  <span>{error}</span>
                </div>
              )}

              <div className="seo-submit-row">
                <p>Hōjin ne publiera rien sans votre approbation.</p>
                <button type="submit" disabled={state === "submitting"}>
                  {state === "submitting" ? (
                    <LoaderCircle className="seo-spin" aria-hidden="true" />
                  ) : (
                    <Sparkles aria-hidden="true" />
                  )}
                  {state === "submitting" ? "Analyse en cours…" : "Analyser et proposer"}
                </button>
              </div>
            </form>
          ) : (
            <section className="seo-panel seo-pending" aria-live="polite">
              <div className="seo-pending-orb">
                <Send aria-hidden="true" />
              </div>
              <p className="seo-panel-overline">Proposition prête</p>
              <h2>À vous de décider.</h2>
              <p>
                L'analyse de <strong>{hostname}</strong> a été envoyée dans votre espace de
                validation en mode{" "}
                <strong>
                  {OPTIMIZATION_MODES.find((mode) => mode.value === optimizationMode)?.label}
                </strong>
                . Vous pouvez corriger la proposition avant d'approuver.
              </p>
              <div className="seo-pending-actions">
                {result?.reviewUrl ? (
                  <a href={result.reviewUrl} target="_blank" rel="noreferrer">
                    Ouvrir la proposition
                    <ExternalLink aria-hidden="true" />
                  </a>
                ) : (
                  <a href={GOTOHUMAN_INBOX_URL} target="_blank" rel="noreferrer">
                    Ouvrir mon espace de validation
                    <ExternalLink aria-hidden="true" />
                  </a>
                )}
                <button
                  type="button"
                  className="seo-text-button"
                  onClick={() => {
                    setState("idle");
                    setResult(null);
                  }}
                >
                  Préparer une autre page
                </button>
              </div>
            </section>
          )}
        </section>

        <section className="seo-recent">
          <div className="seo-recent-heading">
            <div>
              <p className="seo-kicker">Activité récente</p>
              <h2>Un historique compréhensible.</h2>
            </div>
            <button type="button" disabled>
              Voir tout <ChevronRight aria-hidden="true" />
            </button>
          </div>
          <div className="seo-empty">
            <span>01</span>
            <div>
              <strong>Votre première optimisation apparaîtra ici.</strong>
              <p>Diagnostic, validation et publication réunis sur une ligne.</p>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

function ConnectionRow({ label, detail }: { label: string; detail: string }) {
  return (
    <div className="seo-connection-row">
      <span className="seo-connection-icon">
        <Check aria-hidden="true" />
      </span>
      <div>
        <strong>{label}</strong>
        <span>{detail}</span>
      </div>
    </div>
  );
}

function Step({
  number,
  label,
  active = false,
}: {
  number: string;
  label: string;
  active?: boolean;
}) {
  return (
    <div className={`seo-step${active ? " is-active" : ""}`}>
      <span>{number}</span>
      <strong>{label}</strong>
    </div>
  );
}
